// /home/decado/point-atlas-beta/client/src/components/AdSlot.js
import React, { useEffect, useMemo, useRef } from "react";

function normalizeSizes(sizes) {
  if (!Array.isArray(sizes) || sizes.length === 0) return [[320, 50]];
  const out = [];
  for (const pair of sizes) {
    if (!Array.isArray(pair) || pair.length < 2) continue;
    const w = Number(pair[0]);
    const h = Number(pair[1]);
    if (!Number.isFinite(w) || !Number.isFinite(h)) continue;
    out.push([w, h]);
  }
  return out.length ? out : [[320, 50]];
}

function isSlotFilled(el) {
  if (!el) return false;
  if (el.querySelector("iframe")) return true;
  if (el.childElementCount > 0) return true;
  const r = el.getBoundingClientRect();
  if (r.height >= 40 && r.width >= 100) {
    if (el.innerHTML && el.innerHTML.trim()) return true;
  }
  return false;
}

function sanitizeTargetingValue(v) {
  if (v == null) return null;

  if (Array.isArray(v)) {
    const cleaned = v
      .map((x) => (x == null ? "" : String(x).trim()))
      .filter((x) => x !== "" && x.toLowerCase() !== "null" && x.toLowerCase() !== "undefined");
    return cleaned.length ? cleaned : null;
  }

  const s = String(v).trim();
  if (!s) return null;
  if (s.toLowerCase() === "null" || s.toLowerCase() === "undefined") return null;
  return s;
}

function sanitizeTargetingObject(obj) {
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) return obj;
  const out = {};
  for (const k of Object.keys(obj)) {
    const raw = obj[k];
    const cleaned = sanitizeTargetingValue(raw);
    if (cleaned == null) continue;
    out[k] = cleaned;
  }
  return out;
}

function isFounderMode() {
  try {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem("__pa_is_founder") === "1";
  } catch {
    return false;
  }
}

function stableStringifyTargeting(obj) {
  try {
    if (!obj || typeof obj !== "object" || Array.isArray(obj)) return "";
    const keys = Object.keys(obj).sort();
    const out = {};
    for (const k of keys) out[k] = obj[k];
    return JSON.stringify(out);
  } catch {
    return "";
  }
}

function clampString(s, maxLen) {
  const str = String(s || "");
  if (str.length <= maxLen) return str;
  return str.slice(0, maxLen);
}

function sanitizeNitroTargetingKey(key) {
  const k = String(key || "").trim();
  if (!k) return null;
  if (!/^[A-Za-z0-9_]{1,30}$/.test(k)) return null;
  return k;
}

function sanitizeNitroTargetingValue(v) {
  if (v == null) return null;
  if (Array.isArray(v)) return null;
  if (typeof v === "object") return null;

  let s = "";

  if (typeof v === "number") {
    if (!Number.isFinite(v)) return null;
    s = String(v);
  } else if (typeof v === "boolean") {
    s = v ? "1" : "0";
  } else {
    s = String(v);
  }

  s = s.trim();
  if (!s) return null;

  const l = s.toLowerCase();
  if (l === "null" || l === "undefined") return null;

  s = s.replace(/[^A-Za-z0-9_.-]/g, "_");
  s = clampString(s, 64);

  return s || null;
}

function sanitizeNitroTargetingObject(obj) {
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) return null;

  const out = {};
  for (const rawKey of Object.keys(obj)) {
    const key = sanitizeNitroTargetingKey(rawKey);
    if (!key) continue;

    const val = sanitizeNitroTargetingValue(obj[rawKey]);
    if (val == null) continue;

    out[key] = val;
  }

  return Object.keys(out).length ? out : null;
}

function installGoogletagTargetingSanitizerOnce() {
  if (typeof window === "undefined") return;

  const w = window;
  if (w.__paGoogletagTargetingSanitizerInstalled) return;
  w.__paGoogletagTargetingSanitizerInstalled = true;

  const start = Date.now();
  const maxMs = 20000;
  const pollMs = 100;

  const tryInstall = () => {
    const gt = w.googletag;
    if (!gt) return false;

    try {
      if (typeof gt.setConfig === "function" && !gt.__paSetConfigWrapped) {
        const originalSetConfig = gt.setConfig.bind(gt);
        gt.setConfig = (cfg) => {
          try {
            if (cfg && typeof cfg === "object") {
              const next = { ...cfg };
              if (next.targeting && typeof next.targeting === "object") {
                next.targeting = sanitizeTargetingObject(next.targeting);
              }
              return originalSetConfig(next);
            }
          } catch {}
          return originalSetConfig(cfg);
        };
        gt.__paSetConfigWrapped = true;
      }

      if (typeof gt.pubads === "function") {
        const pubads = gt.pubads();
        if (pubads && typeof pubads.setTargeting === "function" && !pubads.__paSetTargetingWrapped) {
          const originalSetTargeting = pubads.setTargeting.bind(pubads);
          pubads.setTargeting = (key, value) => {
            try {
              const cleaned = sanitizeTargetingValue(value);
              if (cleaned == null) return pubads;
              return originalSetTargeting(key, cleaned);
            } catch {
              return originalSetTargeting(key, value);
            }
          };
          pubads.__paSetTargetingWrapped = true;
        }
      }
    } catch {}

    return true;
  };

  if (tryInstall()) return;

  const t = w.setInterval(() => {
    if (tryInstall()) {
      w.clearInterval(t);
      return;
    }
    if (Date.now() - start >= maxMs) {
      w.clearInterval(t);
    }
  }, pollMs);
}

installGoogletagTargetingSanitizerOnce();

function getGlobalAdGuard() {
  if (typeof window === "undefined") return null;
  if (!window.__paNitroAdGuards) window.__paNitroAdGuards = {};
  return window.__paNitroAdGuards;
}

export default function AdSlot({
  id = "Sidebar_banner",
  sizes = [[320, 50]],
  report = {
    enabled: true,
    icon: true,
    wording: "Report Ad",
    position: "top-right",
  },
  className = "",
  style = undefined,
  pollMs = 150,
  maxWaitMs = 10000,
  clearContainerBeforeCreate = true,

  refreshLimit = 20,
  refreshTime = 30,
  refreshVisibleOnly = true,

  targeting = null,

  earlyRetryMs = 2500,
  earlyRetryAttempts = 3,
}) {
  const didInitRef = useRef(false);
  const timerRef = useRef(null);
  const loggedErrorRef = useRef(false);
  const founderRef = useRef(false);

  const retryTimerRef = useRef(null);
  const retryAttemptRef = useRef(0);
  const createdRef = useRef(false);

  const normalizedSizes = useMemo(() => normalizeSizes(sizes), [sizes]);
  const targetingKey = useMemo(() => stableStringifyTargeting(targeting || {}), [targeting]);
  const nitroTargeting = useMemo(
    () => sanitizeNitroTargetingObject(targeting || null),
    [targetingKey, targeting]
  );

  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;
    founderRef.current = isFounderMode();

    const guards = getGlobalAdGuard();
    if (guards) {
      if (guards[id]) {
        return () => {};
      }
      guards[id] = true;
    }

    const startedAt = Date.now();

    const tryNitroRefresh = () => {
      const nitro = window.nitroAds;
      if (!nitro) return false;

      try {
        if (typeof nitro.refresh === "function") {
          nitro.refresh(id);
          return true;
        }
        if (typeof nitro.reload === "function") {
          nitro.reload(id);
          return true;
        }
        if (typeof nitro.requestAd === "function") {
          nitro.requestAd(id);
          return true;
        }
        return false;
      } catch {
        return false;
      }
    };

    const scheduleFillRetry = () => {
      const base = Number(earlyRetryMs) || 0;
      const maxAttempts = Number.isFinite(Number(earlyRetryAttempts)) ? Number(earlyRetryAttempts) : 3;
      if (base <= 0 || maxAttempts <= 0) return;

      const el = document.getElementById(id);
      if (!el) return;

      if (retryTimerRef.current) return;

      const tick = () => {
        retryTimerRef.current = null;

        const elNow = document.getElementById(id);
        if (!elNow) return;

        if (isSlotFilled(elNow)) return;

        retryAttemptRef.current += 1;
        const attempt = retryAttemptRef.current;

        if (founderRef.current) {
          console.warn("[AdSlot] empty slot, retrying", {
            id,
            attempt,
            maxAttempts,
            rect: elNow.getBoundingClientRect(),
          });
        }

        tryNitroRefresh();

        if (attempt >= maxAttempts) return;

        const nextDelay = base * Math.max(1, attempt);
        retryTimerRef.current = window.setTimeout(tick, nextDelay);
      };

      retryAttemptRef.current = 0;
      retryTimerRef.current = window.setTimeout(tick, base);
    };

    const create = () => {
      const el = document.getElementById(id);
      if (!el) return false;

      if (isSlotFilled(el)) {
        createdRef.current = true;
        return true;
      }

      const nitro = window.nitroAds;
      if (!nitro || typeof nitro.createAd !== "function") return false;

      const rect = el.getBoundingClientRect();
      if (founderRef.current) {
        console.log("[AdSlot] attempting createAd", {
          id,
          sizes: normalizedSizes,
          targeting: nitroTargeting || null,
          rect,
        });
      }

      if (clearContainerBeforeCreate) el.innerHTML = "";

      try {
        const rtRaw = Number(refreshTime);
        const rt = Number.isFinite(rtRaw) ? rtRaw : 30;

        const opts = {
          sizes: normalizedSizes,
          report,
          refreshLimit: Number.isFinite(Number(refreshLimit)) ? Number(refreshLimit) : 20,
          refreshTime: rt,
          refreshVisibleOnly: !!refreshVisibleOnly,
        };

        if (nitroTargeting) {
          opts.targeting = nitroTargeting;
        }

        nitro.createAd(id, opts);

        createdRef.current = true;
        scheduleFillRetry();
        return true;
      } catch (e) {
        if (!loggedErrorRef.current) {
          loggedErrorRef.current = true;
          console.warn("[AdSlot] nitroAds.createAd threw for", id, e);
        }
        return false;
      }
    };

    const cleanup = () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
      if (retryTimerRef.current) window.clearTimeout(retryTimerRef.current);

      try {
        const nitro = window.nitroAds;
        if (nitro) {
          if (typeof nitro.destroyAd === "function") nitro.destroyAd(id);
          else if (typeof nitro.destroy === "function") nitro.destroy(id);
        }
      } catch {}

      const g = getGlobalAdGuard();
      if (g && g[id]) delete g[id];
    };

    if (create()) {
      return cleanup;
    }

    timerRef.current = window.setInterval(() => {
      if (create()) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
        return;
      }
      if (Date.now() - startedAt >= (Number(maxWaitMs) || 10000)) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;

        if (founderRef.current && !createdRef.current) {
          console.warn("[AdSlot] gave up before createAd was possible", {
            id,
            hasNitro: !!(window.nitroAds && typeof window.nitroAds.createAd === "function"),
          });
        }
      }
    }, Number(pollMs) || 150);

    return cleanup;
  }, [
    id,
    normalizedSizes,
    report,
    pollMs,
    maxWaitMs,
    clearContainerBeforeCreate,
    refreshLimit,
    refreshTime,
    refreshVisibleOnly,
    targetingKey,
    targeting,
    nitroTargeting,
    earlyRetryMs,
    earlyRetryAttempts,
  ]);

  return <div id={id} className={className} style={style} />;
}
